import { useMemo } from 'react';
import { detectCombatLayout, buildAttackProfile } from '../utils/combatLayoutDetector';
import type { CombatLayoutType, AttackProfile } from '../utils/combatLayoutDetector';

export type CombatLayoutPreference = 'auto' | 'combat-priority' | 'tab-within-tab';

interface UseCombatLayoutResult {
  layout: CombatLayoutType;
  profile: AttackProfile;
  autoDetectedLayout: CombatLayoutType;
  isManualOverride: boolean;
}

/**
 * Hook to determine the combat layout based on attack and spell data
 *
 * @param attackCount - Number of attacks available
 * @param spellIds - Array of spell IDs (cantrips + known + prepared)
 * @param spellSlots - Spell slots by level
 * @param preference - User's layout preference ('auto' for adaptive detection)
 * @returns Layout information including detected layout and profile
 */
export function useCombatLayout(
  attackCount: number,
  spellIds: string[],
  spellSlots: Record<string, { max: number; used: number }> | undefined,
  preference: CombatLayoutPreference = 'auto'
): UseCombatLayoutResult {
  const profile = useMemo(
    () => buildAttackProfile(attackCount, spellIds, spellSlots),
    [attackCount, spellIds, spellSlots]
  );

  const autoDetectedLayout = useMemo(
    () => detectCombatLayout(attackCount, spellIds, spellSlots),
    [attackCount, spellIds, spellSlots]
  );

  const layout = useMemo(() => {
    // Manual override takes precedence
    if (preference === 'combat-priority') return 'combat-priority';
    if (preference === 'tab-within-tab') return 'tab-within-tab';

    // Auto mode uses detection
    return autoDetectedLayout;
  }, [preference, autoDetectedLayout]);

  const isManualOverride = preference !== 'auto';

  return {
    layout,
    profile,
    autoDetectedLayout,
    isManualOverride,
  };
}
